<template>
    <div class="table">
        <NoData v-if="!customers.length" :type="'leads'" />
        <template v-else>
            <div class="table__header box box--default">
                <div class="table__label table__label--xsmall">
                    <div class="checkbox">
                        <input type="checkbox" id="selectAll" class="checkbox__checkbox" :checked="allSelected"
                            :indeterminate.prop="someSelected" @change="toggleAll">
                        <label for="selectAll" class="checkbox__label"></label>
                    </div>
                </div>
                <div class="table__label table__label--small">
                    {{ $t(`leads.${"tableHeader"}.${"date"}`) }}
                </div>
                <div class="table__label table__label--medium">
                    {{ $t(`leads.${"tableHeader"}.${"status"}`) }}
                </div>
                <div class="table__label table__label--small">
                    {{ $t(`accountDetails.${"customerLeadsTable"}.${"header"}.${"referal"}`) }}
                </div>
                <div class="table__label table__label--medium">
                    {{ $t(`leads.${"tableHeader"}.${"name"}`) }}
                </div>
                <div class="table__label table__label--small">
                    {{ $t(`leads.${"tableHeader"}.${"contact"}`) }}
                </div>
                <div class="table__label table__label--medium">
                    Agent
                </div>
                <div class="table__label table__label--small">
                    {{ $t(`leads.${"tableHeader"}.${"zipCode"}`) }}
                </div>
                <div class="table__label table__label--medium">
                    {{ $t(`leads.${"tableHeader"}.${"city"}`) }}
                </div>
                <div class="table__label table__label--small">
                    {{ $t(`leads.${"tableHeader"}.${"deadline"}`) }}
                </div>
                <div class="table__label table__label--medium">
                    {{ $t(`leads.${"tableHeader"}.${"text"}`) }}
                </div>
                <div class="table__label table__label--small"> </div>
                <div class="table__label table__label--small"> </div>
            </div>

            <div class="table__body">
                <div v-for="customer in customers" :key="customer.id">
                    <div @click="routeToCustomer(customer.id)" class=" customer row row--hover box box--default">
                        <div @click.stop class="row__data row__data--name row__data--xsmall center-class">
                            <div class="checkbox" @click.stop>
                                <input type="checkbox" :id="'customer-' + customer.id" class="checkbox__checkbox"
                                    :checked="isSelected(customer.id)" @change="toggleCustomer(customer.id)">
                                <label :for="'customer-' + customer.id" class="checkbox__label"></label>
                            </div>
                        </div>
                        <div class="row__data row__data--name row__data--small center-class">
                            <span v-if="customer && customer.created_at">{{ formatSimpleDate(customer.created_at)
                                }}</span>
                            <span v-else> ----- </span>
                        </div>
                        <div class="row__data row__data--name row__data--medium center-class">
                            <div v-if="customer && customer.customer_sub_status">
                                <span class="status" :class="'status--' + customer.customer_sub_status.id"
                                    v-if="customer.customer_sub_status">{{ customer.customer_sub_status.name }}</span>
                            </div>
                            <span v-else>-----</span>
                        </div>
                        <div @click.stop class="row__data row__data--name row__data--small center-class referal-column">
                            <div v-if="customer.latest_lead && customer.latest_lead.referral">
                                {{ customer.latest_lead.referral.name }}
                            </div>
                            <span v-else>------</span>
                            <div class="referal-note" v-if="customer.latest_lead && customer.latest_lead.referral_note">
                                {{ customer.latest_lead.referral_note }}</div>
                        </div>

                        <div class="row__data row__data--name row__data--medium">
                            <span v-if="customer"> {{ customer.full_name }}
                            </span>
                            <span v-else> ----- </span>
                        </div>
                        <div @click.stop class="row__data row__data--contact row__data--small"> 
                            <div v-if="customer && getPhone(customer)" class="contact-wrapper">
                                <a class="row__phone icon__phone"
                                    :href="getPhone(customer) ? `tel:${getPhone(customer)}` : 'javascript:void(0)'"
                                    :class="{ 'icon__phone--active': getPhone(customer) }"></a>
                                <a :href="getPhone(customer) ? `tel:${getPhone(customer)}` : 'javascript:void(0)'" class="tooltip">{{ getPhone(customer) }}</a>
                            </div>
                            <div v-else>
                                <a class="row__phone icon__phone"></a>
                            </div>

                            <div v-if="customer && getEmail(customer)" class="contact-wrapper">
                                <a class="row__mail icon__mail"
                                    :href="getEmail(customer) ? `mailto:${getEmail(customer)}` : 'javascript:void(0)'"
                                    :class="{ 'icon__mail--active': getEmail(customer) }"></a>
                                <a :href="getEmail(customer) ? `mailto:${getEmail(customer)}` : 'javascript:void(0)'" class="tooltip">{{ getEmail(customer) }}</a>
                            </div>
                            <div v-else> 
                                <a class="row__mail icon__mail"></a>
                            </div>
                        </div>

                        <div class="row__data row__data--name row__data--medium">
                            <span v-if="customer.latest_lead && customer.latest_lead.agent"> {{
                                customer.latest_lead.agent.first_name }} {{ customer.latest_lead.agent.last_name }}
                            </span>
                            <span v-else> ----- </span>
                        </div>

                        <div class="row__data row__data--zip row__data--small center-class">
                            <template v-if="customer && getFirstAddress(customer)">
                                {{ getFirstAddress(customer).zip || '----' }}
                            </template>
                            <template v-else>----</template>
                        </div>
                        <div class="row__data row__data--city row__data--medium center-class">
                            <template v-if="customer && getFirstAddress(customer)">
                                {{ getFirstAddress(customer).city || '----' }}
                            </template>
                            <template v-else>----</template>
                        </div>
                        <div class="row__data row__data--date row__data--small center-class">
                            <span v-if="customer.latest_lead && customer.latest_lead.deadline">{{
                                formatSimpleDate(customer.latest_lead.deadline)
                            }}</span>
                            <span v-else> ----- </span>
                        </div>
                        <div class="row__data row__data--date row__data--medium">
                            <EditableComment v-if="customer.latest_lead && customer.latest_lead.comment"
                                :comment="customer.latest_lead.comment" :readonly="true" />
                            <span v-else> ----- </span>
                        </div>

                        <div @click.stop class="row__data row__data--file row__data--small "
                            style="justify-content: space-between !important;">
                            <div class="icon__pdf" v-if="$checkRolePermission('customer_edit')"
                                :class="{ 'icon__pdf--active': customer.latest_lead && customer.latest_lead.files && customer.latest_lead.files.length > 0 }"
                                @click.stop="openLeadFilesModal(customer.latest_lead)">
                            </div>

                            <div v-if="$checkRolePermission('customer_create')" class="action icon icon__add"
                                @click.stop="openAddLeadModal(customer)"></div>
                        </div>

                        <div class="row__data row__data--small row__data--action"
                            v-if="$checkRolePermission('customer_edit') || $checkRolePermission('customer_delete')"
                            @click.stop="toggleActionsDropdown(customer.id)">
                            <RowActionDropdown v-if="showActionsDropdown === customer.id"
                                @toggleDropdown="handleDropdownToggle($event, customer.id)"
                                :onEdit="() => openEditLeadCustomerModal(customer)"
                                :onDelete="() => { showActionsDropdown = null; activeDeleteId = customer.id; }" />

                            <transition name="fade">
                                <div class="delete" v-if="activeDeleteId === customer.id" @click.stop>
                                    <div class="delete__text">
                                        {{ $t('deleteModal.question') }} {{ $t('deleteModal.lead') }}?
                                    </div>
                                    <div class="delete__buttons">
                                        <div class="button button--confirm" @click.stop="confirmDelete(customer.id)">
                                        </div>
                                        <div class="button button--cancel" @click.stop="activeDeleteId = null">
                                        </div>
                                    </div>
                                </div>
                            </transition>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import RowActionDropdown from "@/components/RowActionDropdown";
import NoData from "@/partials/NoData";
import EditableComment from "@/partials/EditableComment";
export default {
    name: "LeadsTable.vue",

    components: {
        NoData,
        EditableComment,
        RowActionDropdown
    },
    computed: {
        ...mapGetters([

            "loggedUser",
        ]),

        allSelected() {
            return this.customers.length > 0 && this.selectedCustomerIds.length === this.customers.length;
        },

        someSelected() {
            return this.selectedCustomerIds.length > 0 && this.selectedCustomerIds.length < this.customers.length;
        },
    },
    props: {
        customers: {
            required: true,
            type: Array,
        },
    },

    data() {
        return {
            showActionsDropdown: null, // Track which customer's dropdown is open (customer ID)
            activeDeleteId: null,
            selectedCategories: {},
            selectedCustomerIds: [],
        };
    },

    watch: {
        customers: {
            handler(newCustomers) {
                if (!newCustomers || !Array.isArray(newCustomers)) return;

                // Clear selections when customers change (e.g., page change)
                this.selectedCustomerIds = [];
                this.$emit('selection-changed', []);

                newCustomers.forEach(customer => {
                    if (customer.latest_lead && customer.latest_lead.branch_categories && customer.latest_lead.branch_categories.length > 0) {
                        if (!this.selectedCategories[customer.id]) {
                            this.$set(this.selectedCategories, customer.id, customer.latest_lead.branch_categories[0].id);
                        }
                    }
                });
            },
            deep: true,
            immediate: true
        },

        selectedCustomerIds(newValue) {
            this.$emit('selection-changed', newValue);
        }
    },

    methods: {
        toggleActionsDropdown(customerId) {
            if (this.showActionsDropdown === customerId) {
                this.showActionsDropdown = null;
            } else {
                this.showActionsDropdown = customerId;
            }
        },

        handleDropdownToggle(isOpen, customerId) {
            if (!isOpen) {
                this.showActionsDropdown = null;
                // Also close delete confirmation if clicking outside
                if (this.activeDeleteId && this.activeDeleteId === customerId) {
                    this.activeDeleteId = null;
                }
            } else {
                this.showActionsDropdown = customerId;
            }
        },

        toggleAll() {
            if (this.allSelected) {
                this.selectedCustomerIds = [];
            } else {
                this.selectedCustomerIds = this.customers.map(customer => parseInt(customer.id, 10));
            }
        },

        toggleCustomer(customerId) {
            const id = parseInt(customerId, 10);
            const index = this.selectedCustomerIds.indexOf(id);
            if (index > -1) {
                this.selectedCustomerIds.splice(index, 1);
            } else {
                this.selectedCustomerIds.push(id);
            }
        },

        isSelected(customerId) {
            return this.selectedCustomerIds.includes(parseInt(customerId, 10));
        },

        routeToCustomer(customerId) {
            this.$router.push({
                name: "AccountDetails",
                params: {
                    entity: "lead",
                    id: customerId,
                },
                query: {
                    tab: 10
                }
            });
        },


        getLeadIconClass(lead) {
            const categoryId = lead.category?.id;
            return `icon__policy--${categoryId || 'krankenkasse'}`;
        },

        leadTypeColor(lead) {
            return lead.priority ? `type-${lead.priority.type}` : null;
        },

        formatSimpleDate(dateString) {
            if (!dateString) return '-----';

            const d = new Date(dateString);

            if (isNaN(d.getTime())) return dateString;

            const day = String(d.getDate()).padStart(2, '0');
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const year = d.getFullYear();

            return `${day}.${month}.${year}`;
        },

        getPhone(customer) {
            const phones = customer?.customer_phones;
            if (Array.isArray(phones) && phones.length > 0) {
                return phones[0].phone || phones[0].number || null;
            }
            return null;
        },

        getEmail(customer) {
            const emails = customer?.customer_emails;
            if (Array.isArray(emails) && emails.length > 0) {
                return emails[0].email || null;
            }
            return null;
        },

        getFirstAddress(customer) {
            const addresses = customer?.customer_addresses;
            return (Array.isArray(addresses) && addresses.length > 0) ? addresses[0] : null;
        },

        async confirmDelete(customerId) {
            this.activeDeleteId = null;
            try {
                await this.$store.dispatch("deleteLeadCustomer", customerId);
                this.$emit("refresh");
            } catch (err) {
                console.error(err);
            }
        },

        async openEditLeadCustomerModal(customer) {
            this.showActionsDropdown = null; // Close dropdown
            this.$store.commit("setLeadCustomerToEdit", customer);
            this.$store.commit('setShowCustomerLeadModal', true);
        },

        openAddLeadModal(customer) {
            this.$store.commit("setCustomerDetails", customer);
            this.$store.commit("setCustomerDetailsId", customer.id);
            this.$store.commit("setShowAddLeadModal", true);
        },

        openLeadFilesModal(lead) {
            this.$store.commit("setShowFilesEditModal", true);
            this.$store.commit("setFilesToEditId", lead.id);
            this.$store.commit("setFilesToEdit", lead.files);
            this.$store.commit("setFilesEditMode", 'Leads');
        },
    },

    mounted() {
        if (!(this.$checkRolePermission('customer_access') || this.$checkRolePermission('customer_show'))) {
            this.$router.push('/dashboard');
            return;
        }
    }
};
</script>

<style scoped lang="scss">
.referal-column {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    .referal-note {
        font-size: 0.7em;
        margin-top: 10px;
    }
}

.row__data--xsmall {
    flex: 0 !important;
}

.actions {
    justify-content: space-between !important;
}

.row__data--delete,
.icon__delete,
.row__data--action {
    position: relative;

    .delete {
        position: absolute;
        z-index: 4;
        background: var(--bg-1);
        right: 0%;
        top: 50%;
        transform: translateY(-50%);
        margin-right: 10px;
        width: 330px;
        display: flex;
        align-items: center;
        border-radius: var(--radius-1);
        border: 1px solid var(--border-2);
        padding: 10px 20px;
        box-shadow: 0px 3px 6px var(--shadow-7);

        &__text {
            line-height: 20px;
            color: var(--text-9);
        }

        &__buttons {
            display: flex;
        }

        .button {
            width: 24px;
            height: 24px;
            cursor: pointer;
            transition: all 0.3s ease;

            &--confirm {
                background: url("../assets/icons/default/confirm_icon.svg");
                margin: 0 15px;

                &:hover {
                    background: url("../assets/icons/default/confirm_hover_icon.svg");
                }
            }

            &--cancel {
                background: url("../assets/icons/default/close_icon.svg");

                &:hover {
                    background: url("../assets/icons/default/close_hover_icon.svg");
                }
            }
        }
    }
}

.table .row__phone,
.table .row__mail,
.icon__pdf,
.icon__pdf--active,
.icon__contract,
.icon__edit,
.icon__delete {
    display: block;
    height: 25px;
    width: 25px;
}

.icon__pdf,

.icon__contract,
.icon__edit,
.icon__delete {
    height: 100%;
}

.row__data--contact span {
    display: flex;
}

.center-class {
    justify-content: center !important;
    text-align: center;
}

.status {
    padding: 8px 16px;
    border-radius: 10px;
    text-align: center;

    //new
    &--1 {
        background: #cfcdcda2;
    }

    //open
    &--2 {
        background: #003cff85;
    }

    //need analyse
    &--3 {
        background: #ffae0062;
    }

    //negotiation
    &--4 {
        background: #ff5e007c;
    }

    //won
    &--5 {
        background: #22db8e1a;
    }

    //lost
    &--6 {
        background: #ff00008f;
    }
}

.table .row__phone {
    margin-right: 20px !important;
}

.action-menu-icon {
    font-size: 24px;
    font-weight: bold;
    cursor: pointer;
    color: var(--text-3);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    transition: all 0.3s ease;

    &:hover {
        background-color: var(--bg-3);
        color: var(--text-1);
    }
}

.row__data--action {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
}

.contact-wrapper {
    position: relative;
    display: inline-block;

    .tooltip {
        visibility: hidden;
        width: 140px; 
        word-break: break-word;
        background-color: var(--bg-17);
        color: var(--text-9);
        display: flex;
        justify-content: center;
        align-items: center;
        border-radius: 6px;
        padding: 8px 5px;
        position: absolute;
        font-size: 0.75em;
        z-index: 1;
        left: 50%;
        transform: translateX(-50%);
        top: -100%;
    }

    &:hover .tooltip {
        visibility: visible;
    }
}
</style>
